import React, { useEffect, useState } from "react";
import {
  Button, Card, CardContent, CardOverflow, DialogContent, DialogTitle,
  Divider, Link, ListItem, Modal, ModalDialog, Stack, Step, StepIndicator,
  Stepper, Typography
} from "@mui/joy";
import { Check } from "@mui/icons-material";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { useMediaQuery } from "@mui/material";
import events from "Data/EventTimeline";
import dinner_menu from "Data/Food";
import app_menu from "Data/Appetizers";
import "./EventStepper.css";

function useCurrentUnixTime() {
  const [unixTime, setUnixTime] = useState(Math.floor(Date.now() / 1000));
  useEffect(() => {
    const interval = setInterval(() => {
      setUnixTime(Math.floor(Date.now() / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  return unixTime;
}

function EventStepper() {
  const [modalState, setModalState] = useState({ open: false, detail: "" });
  const isMobile = useMediaQuery('(max-width:600px)');
  const current_time = useCurrentUnixTime();

  const handleDetailModal = (description) => {
    setModalState({ open: true, detail: description });
  };

  const convertUnixToDate = (unixTimestamp) => {
    const date = new Date(unixTimestamp * 1000);
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
      timeZoneName: "short"
    }).format(date);
  };

  const getStepStatus = (start, end) => {
    if (current_time >= end) return "finished";
    if (current_time >= start && current_time <= end) return "in_progress";
    return "not_completed";
  };

  return (
    <div className="stepper-card">
      <Card sx={{ backgroundColor: "#BF5B05", border: "none" }}>
        <Typography level="h1" sx={{ textAlign: 'center', color: "#fff", mb: 2 }}>
          Event Timeline
        </Typography>

        <Modal open={modalState.open} onClose={() => setModalState({ open: false, detail: "" })}>
          <ModalDialog
            sx={{
              borderRadius: 12,
              padding: 3,
              backgroundColor: "#fff",
              maxWidth: { xs: 300, sm: 400 },
              width: "90%"
            }}
          >
            <DialogTitle>Details:</DialogTitle>
            <DialogContent>
  {(() => {
    switch (modalState.detail) {
      case "dinner":
        return (
          <Card sx={{ border: "none", boxShadow: "none", backgroundColor: "#fff" }}>
            <Typography level="title-md" sx={{ mb: 2 }}>Dinner Menu</Typography>
            <Stack spacing={2}>
              {dinner_menu.map((item) => (
                <Card key={item.name} variant="outlined" sx={{ borderRadius: 4 }}>
                  <CardContent>
                    <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                      <Link href={item.link} target="_blank" rel="noopener noreferrer">
                        {item.name} 
                      </Link>
                    </Typography>
                    <Typography level="body-sm" sx={{ mb: 1 }}>{item.description}</Typography>
                    {item.allergies.length > 0 && (
                      <Typography level="body-xs" color="danger">
                        ⚠️ Allergens: {item.allergies.join(", ")}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Card>
        );

      case "Cocktail Hour":
        return (
          <Card sx={{ border: "none", boxShadow: "none", backgroundColor: "#fff" }}>
            <Typography level="title-md" sx={{ mb: 2 }}>Cocktail Hour Appetizers</Typography>
            <Stack spacing={2}>
              {app_menu.map((item) => (
                <Card key={item.name} variant="outlined" sx={{ borderRadius: 4 }}>
                  <CardContent>
                    <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                      <Link href={item.link} target="_blank" rel="noopener noreferrer">
                        {item.name}
                      </Link>
                    </Typography>
                    <Typography level="body-sm" sx={{ mb: 1 }}>{item.description}</Typography>
                    {item.allergies.length > 0 && (
                      <Typography level="body-xs" color="danger">
                        ⚠️ Allergens: {item.allergies.join(", ")}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Card>
        );

      default:
        return modalState.detail.split("\n").map((paragraph, index) =>
          paragraph.startsWith("-") ? (
            <ListItem key={index}>{paragraph.substring(2)}</ListItem>
          ) : (
            <Typography key={index} level="body-sm" sx={{ whiteSpace: "pre-line", mb: 2 }}>
              {paragraph}
            </Typography>
          )
        );
    }
  })()}
</DialogContent>

          </ModalDialog>
        </Modal>

        <Stepper
          orientation={isMobile ? "vertical" : "horizontal"}
          sx={{ overflowX: 'auto', display: 'flex', flexWrap: 'nowrap' }}
        >
          {events.map((e) => {
            const status = getStepStatus(e.start_time, e.end_time);
            const indicator = {
              not_completed: <AccessTimeIcon />,
              in_progress: <PriorityHighIcon />,
              finished: <Check />
            }[status];

            return (
              <Step key={e.title} indicator={<StepIndicator color="primary" variant='outlined'>{indicator}</StepIndicator>}>
                <Card
                  orientation="vertical"
                  sx={{
                    backgroundColor: "#BF5B05",
                    borderRadius: 6,
                    padding: 3,
                    boxShadow: "lg",
                    border: ".5px solid #111",
                    transition: "transform 0.2s ease-in-out",
                    "&:hover": {
                      transform: "scale(1.1)",
                      boxShadow: "xl"
                    }
                  }}
                >
                  <CardContent sx={{ textDecoration: status === 'finished' ? "line-through" : "none" }}>
                    <Typography level="title-sm" sx={{ color: '#fff', fontWeight: 500 }}>
                      {e.title}
                    </Typography>
                  </CardContent>
                  <CardOverflow variant="soft">
                    <Divider inset="context" />
                    <CardContent>
                      <Stack spacing={2} direction="row" sx={{ justifyContent: "center", alignItems: "center" }}>
                        <Typography level="body-xs">
                          {convertUnixToDate(e.start_time)} – {convertUnixToDate(e.end_time)}
                        </Typography>
                        <Button onClick={() => handleDetailModal(e.description)} size="sm" variant="outlined">
                          Details
                        </Button>
                      </Stack>
                    </CardContent>
                  </CardOverflow>
                </Card>
              </Step>
            );
          })}
        </Stepper>
      </Card>
    </div>
  );
}

export default EventStepper;
